import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import {
    fetchHourReportingSandboxWorkerOptions,
    getHourReportingSandboxOpenWorkflow,
    HourReportingSandboxRequest,
    HourReportingSandboxResult,
    HourReportingSandboxTranscriptMessage,
    HourReportingSandboxWorkerOption,
    resetHourReportingSandboxWorkflow,
    sendHourReportingSandboxMessage,
} from '../api/hourReportingSandboxApi';

type SandboxChatMessage = {
    id: string;
    sender: 'assistant' | 'user' | 'system';
    text: string;
    createdAt: string;
    options?: Array<{ id: string; title: string; kind: 'button' | 'list' }>;
};

type WorkflowOperatorStatus = {
    easyflexLinkPresent?: boolean;
    imageSubmitted?: boolean;
    userConfirmed?: boolean;
};

type WorkerPhoneSuggestion = HourReportingSandboxWorkerOption;

const DEFAULT_TIMESHEET_QUESTION_OVERRIDE = JSON.stringify([
    {
        questionKey: 'is_hourpaper',
        questionName: 'Is this an hourpaper',
        question: 'Does this file primarily show an hourpaper, timesheet, or worked-hours registration document? Return true only if it is really an hour registration document.',
        answerType: 'BOOLEAN',
        questionDataKey: 'is_hourpaper',
        isRequiredAnswer: true,
    },
    {
        questionKey: 'lines',
        questionName: 'Worked days',
        question: 'Extract every worked day from the hourpaper. Return one aggregated item per date. Include only days that clearly contain worked hours.',
        answerType: 'LIST_OBJECT',
        questionDataKey: 'lines',
        isRequiredAnswer: true,
        minItems: 1,
        listItemSchema: [
            {
                fieldName: 'date',
                label: 'Worked date',
                fieldType: 'DATE',
                isRequired: true,
                helpText: 'Return the work date in ISO format YYYY-MM-DD.',
            },
            {
                fieldName: 'start_time',
                label: 'Start time',
                fieldType: 'TEXT',
                isRequired: true,
                helpText: 'Return the shift start time in HH:MM 24-hour format if possible.',
            },
            {
                fieldName: 'end_time',
                label: 'End time',
                fieldType: 'TEXT',
                isRequired: true,
                helpText: 'Return the shift end time in HH:MM 24-hour format if possible.',
            },
            {
                fieldName: 'break_minutes',
                label: 'Break minutes',
                fieldType: 'NUMBER',
                isRequired: false,
                helpText: 'Return the unpaid break in whole minutes when available.',
            },
            {
                fieldName: 'remarks',
                label: 'Remarks',
                fieldType: 'TEXT',
                isRequired: false,
                helpText: 'Optional day-specific remark or note from the document.',
            },
        ],
    },
    {
        questionKey: 'header_comment',
        questionName: 'Header comment',
        question: 'Extract a general comment that applies to the full hourpaper, if present.',
        answerType: 'TEXT',
        questionDataKey: 'header_comment',
        isRequiredAnswer: false,
    },
    {
        questionKey: 'project_notes',
        questionName: 'Project notes',
        question: 'Extract any project-wide note or operator note that is relevant to the declaration.',
        answerType: 'TEXT',
        questionDataKey: 'project_notes',
        isRequiredAnswer: false,
    },
], null, 2);

type DraftCommandLine = {
    date: string;
    startTime: string | null;
    endTime: string | null;
    breakMinutes: number | null;
    workedMinutes: number | null;
    remarks: string | null;
    looncomponentId: number | null;
};

function formatWorkedMinutes(minutes: number | null | undefined): string {
    if (!Number.isFinite(Number(minutes)) || Number(minutes) <= 0) {
        return 'missing';
    }

    const value = Number(minutes);
    return `${Math.floor(value / 60)}h ${value % 60}m`;
}

function extractDraftCommandLines(command: Record<string, any> | null | undefined): DraftCommandLine[] {
    const draftLines = Array.isArray(command?.draft_lines) ? command.draft_lines : [];
    const commandLines = Array.isArray(command?.lines) ? command.lines : [];
    const sourceLines = draftLines.length ? draftLines : commandLines;

    return sourceLines.map((draftLine: any, index: number) => {
        const matchedCommandLine = commandLines.find((line: any) => line?.date === draftLine?.date) || commandLines[index] || null;
        return {
            date: String(draftLine?.date || matchedCommandLine?.date || ''),
            startTime: draftLine?.start_time || null,
            endTime: draftLine?.end_time || null,
            breakMinutes: draftLine?.break_minutes ?? null,
            workedMinutes: matchedCommandLine?.minutes ?? null,
            remarks: draftLine?.remarks || null,
            looncomponentId: matchedCommandLine?.looncomponentId ?? null,
        };
    }).filter((line) => Boolean(line.date));
}

function formatWorkerName(worker: Partial<WorkerPhoneSuggestion> | null | undefined): string {
    const firstName = String(worker?.firstName || '').trim();
    const lastName = String(worker?.lastName || '').trim();
    return [firstName, lastName].filter(Boolean).join(' ') || 'Unknown worker';
}

function formatWorkerSuggestionLabel(worker: Partial<WorkerPhoneSuggestion> | null | undefined): string {
    const phone = String(worker?.preferredPhone || worker?.whatsappId || worker?.mobileNumber || worker?.phoneNumber || '').trim() || 'No phone';
    const name = formatWorkerName(worker);
    const nationality = String(worker?.nationality || '').trim() || 'Nationality unknown';
    return `${phone} · ${name} · ${nationality}`;
}

const HourReportingSandboxPage: React.FC = () => {
    const [phoneNumber, setPhoneNumber] = useState('31600000000');
    const [phoneFieldFocused, setPhoneFieldFocused] = useState(false);
    const [phoneOptions, setPhoneOptions] = useState<WorkerPhoneSuggestion[]>([]);
    const [isLoadingPhoneOptions, setIsLoadingPhoneOptions] = useState(false);
    const [placementNumber, setPlacementNumber] = useState('');
    const [declarationId, setDeclarationId] = useState('');
    const [extraInstructions, setExtraInstructions] = useState('');
    const [questionsOverrideJson, setQuestionsOverrideJson] = useState('');
    const [draftMessage, setDraftMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [transcript, setTranscript] = useState<SandboxChatMessage[]>([]);
    const [workflowResult, setWorkflowResult] = useState<HourReportingSandboxResult | null>(null);
    const [lastSandboxRequest, setLastSandboxRequest] = useState<Record<string, any> | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!phoneFieldFocused) {
            return;
        }

        let cancelled = false;
        const timeoutId = window.setTimeout(async () => {
            setIsLoadingPhoneOptions(true);
            try {
                const result = await fetchHourReportingSandboxWorkerOptions(phoneNumber.trim() || undefined, 8);
                if (cancelled) {
                    return;
                }
                setPhoneOptions(
                    result.filter((worker) =>
                        Boolean(
                            String(
                                worker.preferredPhone || worker.whatsappId || worker.mobileNumber || worker.phoneNumber || '',
                            ).trim(),
                        ),
                    ),
                );
            } catch (err) {
                if (!cancelled) {
                    setPhoneOptions([]);
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingPhoneOptions(false);
                }
            }
        }, 250);

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [phoneFieldFocused, phoneNumber]);

    const selectedWorkerOption = useMemo(
        () =>
            phoneOptions.find(
                (worker) =>
                    String(
                        worker.preferredPhone || worker.whatsappId || worker.mobileNumber || worker.phoneNumber || '',
                    ).trim() === phoneNumber.trim(),
            ) || null,
        [phoneOptions, phoneNumber],
    );

    const workflow = workflowResult?.workflow || null;
    const statusChips = useMemo(() => {
        const operatorStatus = (workflow?.operatorStatus || {}) as WorkflowOperatorStatus;
        return [
            { label: workflow?.workflowState || 'no workflow', color: 'default' as const },
            { label: workflow?.workflowLanguage ? `Lang ${workflow.workflowLanguage}` : 'Lang unknown', color: workflow?.workflowLanguage ? 'info' as const : 'default' as const },
            { label: operatorStatus.easyflexLinkPresent ? 'Easyflex linked' : 'Link missing', color: operatorStatus.easyflexLinkPresent ? 'success' as const : 'default' as const },
            { label: operatorStatus.imageSubmitted ? 'Image submitted' : 'No image', color: operatorStatus.imageSubmitted ? 'success' as const : 'default' as const },
            { label: operatorStatus.userConfirmed ? 'Confirmed' : 'Awaiting confirmation', color: operatorStatus.userConfirmed ? 'success' as const : 'default' as const },
        ];
    }, [workflow]);

    const appendAssistantMessages = (messages: HourReportingSandboxTranscriptMessage[]) => {
        setTranscript((current) => [
            ...current,
            ...messages.map((message, index) => ({
                id: `${message.id}-${message.createdAt}-${current.length + index}`,
                sender: 'assistant' as const,
                text: message.text,
                createdAt: message.createdAt,
                options: extractActions(message),
            })),
        ]);
    };

    const extractionDebug = workflow?.workflowContext?.lastExtractionDebug || null;
    const currentCommand = (workflow?.confirmedFields || workflow?.extractedFields || null) as Record<string, any> | null;
    const commandLines = useMemo(() => extractDraftCommandLines(currentCommand), [currentCommand]);
    const totalWorkedMinutes = useMemo(
        () => commandLines.reduce((sum, line) => sum + Math.max(0, Number(line.workedMinutes || 0)), 0),
        [commandLines],
    );
    const observability = workflow?.observability || null;

    const buildRequestBase = (): Pick<HourReportingSandboxRequest, 'phoneNumber' | 'placementNumber' | 'declarationId'> => ({
        phoneNumber,
        placementNumber: placementNumber && Number.isFinite(Number(placementNumber)) ? Number(placementNumber) : null,
        declarationId: declarationId.trim() || null,
    });

    const readFileAsBase64 = async (file: File): Promise<string> => {
        const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(new Error('Failed to read upload'));
            reader.readAsDataURL(file);
        });
        const [, base64 = ''] = dataUrl.split(',');
        return base64;
    };

    const handleSend = async (override?: Partial<HourReportingSandboxRequest>, userTextOverride?: string) => {
        if (!phoneNumber.trim()) {
            setError('Phone number is required');
            return;
        }

        if (
            !draftMessage.trim() &&
            !selectedFile &&
            !String(override?.body || '').trim() &&
            !override?.interactiveButtonId &&
            !override?.interactiveListId
        ) {
            setError('Enter a message or choose a file');
            return;
        }

        setIsSending(true);
        setError(null);

        try {
            const parsedQuestionsOverride = questionsOverrideJson.trim() ? JSON.parse(questionsOverrideJson) : null;
            if (parsedQuestionsOverride !== null && !Array.isArray(parsedQuestionsOverride)) {
                throw new Error('Structured questions override must be a JSON array');
            }
            const payload: HourReportingSandboxRequest = {
                ...buildRequestBase(),
                body: override?.body ?? (draftMessage.trim() || undefined),
                interactiveButtonId: override?.interactiveButtonId,
                interactiveListId: override?.interactiveListId,
                interactiveTitle: override?.interactiveTitle,
                workflowId: workflowResult?.workflowId || undefined,
                extractionOverrides: {
                    extraInstructions: extraInstructions.trim() || null,
                    questionsOverride: parsedQuestionsOverride,
                    debug: true,
                },
            };

            if (selectedFile) {
                payload.fileData = {
                    fileName: selectedFile.name,
                    mimeType: selectedFile.type || 'application/octet-stream',
                    contentBase64: await readFileAsBase64(selectedFile),
                    sizeBytes: selectedFile.size,
                    type: selectedFile.type.startsWith('image/') ? 'image' : 'document',
                };
            }

            const userText = userTextOverride ?? (draftMessage.trim() || (selectedFile ? `Uploaded ${selectedFile.name}` : 'Selected interactive reply'));
            setTranscript((current) => [
                ...current,
                {
                    id: `user-${Date.now()}`,
                    sender: 'user',
                    text: userText,
                    createdAt: new Date().toISOString(),
                },
            ]);

            const result = await sendHourReportingSandboxMessage(payload);
            setLastSandboxRequest(payload);
            setWorkflowResult(result);
            appendAssistantMessages(result.messages || []);
            setDraftMessage('');
            setSelectedFile(null);
        } catch (err: any) {
            setError(err?.message || 'Failed to send sandbox message');
        } finally {
            setIsSending(false);
        }
    };

    const handleLoadOpenWorkflow = async () => {
        if (!phoneNumber.trim()) {
            setError('Phone number is required');
            return;
        }

        setIsLoadingWorkflow(true);
        setError(null);
        try {
            const result = await getHourReportingSandboxOpenWorkflow(phoneNumber);
            setWorkflowResult(result);
            setTranscript((current) => [
                ...current,
                {
                    id: `system-${Date.now()}`,
                    sender: 'system',
                    text: result.workflowId
                        ? `Loaded workflow ${result.workflowId} in state ${result.workflow?.workflowState || 'unknown'}.`
                        : 'No open workflow found for this phone number.',
                    createdAt: new Date().toISOString(),
                },
            ]);
        } catch (err: any) {
            setError(err?.message || 'Failed to load workflow');
        } finally {
            setIsLoadingWorkflow(false);
        }
    };

    const handleResetOpenWorkflow = async () => {
        if (!phoneNumber.trim()) {
            setError('Phone number is required');
            return;
        }

        setIsLoadingWorkflow(true);
        setError(null);
        try {
            const result = await resetHourReportingSandboxWorkflow(phoneNumber);
            setWorkflowResult(null);
            setTranscript((current) => [
                ...current,
                {
                    id: `system-reset-${Date.now()}`,
                    sender: 'system',
                    text: result.workflowId
                        ? `Reset workflow ${result.workflowId}.`
                        : 'No open workflow to reset for this phone number.',
                    createdAt: new Date().toISOString(),
                },
            ]);
        } catch (err: any) {
            setError(err?.message || 'Failed to reset workflow');
        } finally {
            setIsLoadingWorkflow(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: '360px minmax(0, 1fr) 360px' },
                gap: 2,
                p: 2,
                minHeight: '100%',
                background: 'linear-gradient(180deg, #efe3c7 0%, #f7f3ea 100%)',
            }}
        >
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #d5c29a', p: 2.5 }}>
                <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: '#87571a' }}>
                    Hour Reporting Sandbox
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Worker Simulator
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Test the real hour-reporting workflow without sending anything to WhatsApp.
                </Typography>

                <Stack spacing={2}>
                    <Autocomplete
                        freeSolo
                        open={phoneFieldFocused}
                        onOpen={() => setPhoneFieldFocused(true)}
                        onClose={() => setPhoneFieldFocused(false)}
                        options={phoneOptions}
                        loading={isLoadingPhoneOptions}
                        filterOptions={(options) => options}
                        getOptionLabel={(option) =>
                            typeof option === 'string'
                                ? option
                                : String(option.preferredPhone || option.whatsappId || option.mobileNumber || option.phoneNumber || '')
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        inputValue={phoneNumber}
                        onInputChange={(_event, value, reason) => {
                            if (reason === 'input' || reason === 'clear') {
                                setPhoneNumber(value);
                            }
                        }}
                        onChange={(_event, value) => {
                            if (typeof value === 'string') {
                                setPhoneNumber(value);
                                return;
                            }
                            setPhoneNumber(
                                String(
                                    value?.preferredPhone || value?.whatsappId || value?.mobileNumber || value?.phoneNumber || '',
                                ).trim(),
                            );
                        }}
                        noOptionsText={phoneNumber.trim() ? 'No linked workers with active placements match this search.' : 'Pick a worker or start typing.'}
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Stack spacing={0.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {String(option.preferredPhone || option.whatsappId || option.mobileNumber || option.phoneNumber || '').trim() || 'No phone'}
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatWorkerName(option)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {String(option.nationality || '').trim() || 'Nationality unknown'}
                                        {option.easyflexRegistrationNumber ? ` · EF ${option.easyflexRegistrationNumber}` : ''}
                                        {option.activePlacementCount > 0 ? ` · ${option.activePlacementCount} active placement${option.activePlacementCount === 1 ? '' : 's'}` : ''}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Phone Number"
                                helperText={
                                    selectedWorkerOption
                                        ? `Selected: ${formatWorkerSuggestionLabel(selectedWorkerOption)}`
                                        : 'Used to resolve a linked worker with an active placement.'
                                }
                                onFocus={() => setPhoneFieldFocused(true)}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoadingPhoneOptions ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <TextField
                        label="Placement Number Override"
                        value={placementNumber}
                        onChange={(event) => setPlacementNumber(event.target.value)}
                        placeholder="Optional Easyflex placement number"
                    />
                    <TextField
                        label="Declaration ID Override"
                        value={declarationId}
                        onChange={(event) => setDeclarationId(event.target.value)}
                        placeholder="Optional declaration id"
                    />
                    <TextField
                        multiline
                        minRows={3}
                        label="AI Extra Instructions"
                        value={extraInstructions}
                        onChange={(event) => setExtraInstructions(event.target.value)}
                        placeholder="Example: Prefer table cells over handwritten margin notes. If multiple shifts are visible, extract only the latest row."
                    />
                    <TextField
                        multiline
                        minRows={6}
                        label="Structured Questions Override (JSON)"
                        value={questionsOverrideJson}
                        onChange={(event) => setQuestionsOverrideJson(event.target.value)}
                        placeholder="Leave empty to use backend defaults"
                    />

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="contained" startIcon={<PlayCircleOutlineOutlinedIcon />} onClick={() => handleSend({ body: 'hours' }, 'hours')} disabled={isSending}>
                            Start Hours Flow
                        </Button>
                        <Button variant="outlined" onClick={() => setQuestionsOverrideJson(DEFAULT_TIMESHEET_QUESTION_OVERRIDE)}>
                            Load Default Schema
                        </Button>
                        <Button variant="outlined" onClick={() => {
                            setQuestionsOverrideJson('');
                            setExtraInstructions('');
                        }}>
                            Reset AI Tweaks
                        </Button>
                        <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={handleLoadOpenWorkflow} disabled={isLoadingWorkflow || isSending}>
                            Load Open Workflow
                        </Button>
                        <Button variant="outlined" color="warning" onClick={handleResetOpenWorkflow} disabled={isLoadingWorkflow || isSending}>
                            Reset Open Workflow
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DeleteSweepOutlinedIcon />}
                            onClick={() => {
                                setTranscript([]);
                                setWorkflowResult(null);
                                setSelectedFile(null);
                                setError(null);
                            }}
                        >
                            Clear View
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #d5c29a', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 640 }}>
                <Box sx={{ p: 2.5, borderBottom: '1px solid #eadfc8', backgroundColor: '#fff9ee' }}>
                    <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: '#87571a' }}>
                        Chat Testbed
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Conversation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Send text, upload hour papers, and click interactive replies as if you were the worker.
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

                <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
                    <Stack spacing={1.5}>
                        {transcript.length === 0 ? (
                            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fffdf8' }}>
                                <Typography color="text.secondary">
                                    Start with "hours" or upload an hourpaper to exercise the workflow.
                                </Typography>
                            </Paper>
                        ) : transcript.map((message) => (
                            <Box
                                key={message.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        maxWidth: '80%',
                                        p: 1.5,
                                        borderRadius: 3,
                                        border: '1px solid',
                                        borderColor: message.sender === 'user' ? '#d7b36d' : '#e4d6bb',
                                        backgroundColor: message.sender === 'user' ? '#f7e2b7' : message.sender === 'system' ? '#f3f0e7' : '#fffdf8',
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                                        {message.sender === 'user' ? <PersonOutlineOutlinedIcon fontSize="small" /> : <SmartToyOutlinedIcon fontSize="small" />}
                                        <Typography variant="caption" color="text.secondary">
                                            {message.sender === 'user' ? 'Worker' : message.sender === 'system' ? 'System' : 'Assistant'} · {new Date(message.createdAt).toLocaleTimeString()}
                                        </Typography>
                                    </Stack>
                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Typography>
                                    {message.options && message.options.length > 0 && (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                                            {message.options.map((option) => (
                                                <Button
                                                    key={option.id}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleSend(
                                                        option.kind === 'button'
                                                            ? { interactiveButtonId: option.id, interactiveTitle: option.title }
                                                            : { interactiveListId: option.id, interactiveTitle: option.title },
                                                        option.title,
                                                    )}
                                                    disabled={isSending}
                                                >
                                                    {option.title}
                                                </Button>
                                            ))}
                                        </Stack>
                                    )}
                                </Paper>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                <Divider />

                <Box sx={{ p: 2.5, backgroundColor: '#fff9ee' }}>
                    <Stack spacing={1.5}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Message"
                            value={draftMessage}
                            onChange={(event) => setDraftMessage(event.target.value)}
                            placeholder='Examples: "hours", "confirm", or "DATE 2026-03-17 07:30-16:15 BREAK 30 REMARK night shift"'
                        />
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                            <Button
                                variant="outlined"
                                startIcon={<UploadFileOutlinedIcon />}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {selectedFile ? 'Replace Upload' : 'Upload Hourpaper'}
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept="image/*,.pdf"
                                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                            />
                            {selectedFile && <Chip label={`${selectedFile.name} (${Math.round(selectedFile.size / 1024)} KB)`} onDelete={() => setSelectedFile(null)} />}
                            <Box sx={{ flex: 1 }} />
                            <Button variant="contained" onClick={() => handleSend()} disabled={isSending}>
                                {isSending ? 'Sending...' : 'Send'}
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #d5c29a', overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, borderBottom: '1px solid #eadfc8', backgroundColor: '#fff9ee' }}>
                    <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: '#87571a' }}>
                        Workflow State
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Debug Panel
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Inspect current progress, confirmed payload, and event trail.
                    </Typography>
                </Box>

                <Box sx={{ p: 2.5 }}>
                    {(isSending || isLoadingWorkflow) && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                                {isSending ? 'Sending sandbox message...' : 'Loading or resetting workflow...'}
                            </Typography>
                        </Stack>
                    )}

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        {statusChips.map((chip) => (
                            <Chip key={chip.label} size="small" label={chip.label} color={chip.color} />
                        ))}
                        <Chip size="small" label={`extract ok ${observability?.extractionSuccesses || 0}/${observability?.extractionAttempts || 0}`} />
                        <Chip size="small" label={`corrections ${observability?.correctionCount || 0}`} />
                        <Chip size="small" label={`confirm ok ${observability?.confirmSuccesses || 0}/${observability?.confirmAttempts || 0}`} />
                    </Stack>

                    {workflow?.operatorStatus?.draftValidationSummary && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {workflow.operatorStatus.draftValidationSummary}
                        </Alert>
                    )}

                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Current command
                    </Typography>
                    {commandLines.length > 0 && (
                        <Stack spacing={1} sx={{ mt: 1.25, mb: 1.5 }}>
                            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#fffdf8' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {commandLines.length} line(s) · {formatWorkedMinutes(totalWorkedMinutes)} total
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Comment: {currentCommand?.header?.comment || currentCommand?.header_comment || currentCommand?.project_notes || 'empty'}
                                </Typography>
                            </Paper>
                            {commandLines.map((line, index) => (
                                <Paper key={`${line.date}-${line.startTime || 'na'}-${line.endTime || 'na'}-${index}`} variant="outlined" sx={{ p: 1.5, backgroundColor: '#fffdf8' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {line.date}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {line.startTime && line.endTime ? `${line.startTime}-${line.endTime}` : 'time missing'} · break {line.breakMinutes ?? 0}m · worked {formatWorkedMinutes(line.workedMinutes)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Looncomponent: {line.looncomponentId || 'missing'}
                                    </Typography>
                                    {line.remarks && (
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {line.remarks}
                                        </Typography>
                                    )}
                                </Paper>
                            ))}
                        </Stack>
                    )}
                    <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#faf7f0' }}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                            {JSON.stringify(currentCommand || {}, null, 2)}
                        </pre>
                    </Paper>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>
                        Last sandbox request
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#faf7f0' }}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                            {JSON.stringify(lastSandboxRequest || {}, null, 2)}
                        </pre>
                    </Paper>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>
                        Extraction debug
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#faf7f0' }}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                            {JSON.stringify(extractionDebug || {}, null, 2)}
                        </pre>
                    </Paper>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>
                        Events
                    </Typography>
                    <Stack spacing={1.25} sx={{ mt: 1.25, maxHeight: 320, overflow: 'auto' }}>
                        {(workflowResult?.events || []).map((event) => (
                            <Paper key={event.id} variant="outlined" sx={{ p: 1.25, backgroundColor: '#fffdf8' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(event.createdAt).toLocaleString()}
                                </Typography>
                                <Typography sx={{ fontWeight: 700 }}>{event.eventType}</Typography>
                                {event.message && <Typography variant="body2">{event.message}</Typography>}
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
};

function extractActions(message: HourReportingSandboxTranscriptMessage): Array<{ id: string; title: string; kind: 'button' | 'list' }> {
    const interactive = message.rawPayload?.interactive;
    if (!interactive) {
        return [];
    }

    if (interactive.type === 'button' && Array.isArray(interactive.action?.buttons)) {
        return interactive.action.buttons
            .map((button: any) => ({
                id: String(button?.reply?.id || ''),
                title: String(button?.reply?.title || ''),
                kind: 'button' as const,
            }))
            .filter((option: { id: string; title: string }) => option.id && option.title);
    }

    if (interactive.type === 'list' && Array.isArray(interactive.action?.sections)) {
        return interactive.action.sections.flatMap((section: any) =>
            Array.isArray(section?.rows)
                ? section.rows
                    .map((row: any) => ({
                        id: String(row?.id || ''),
                        title: String(row?.title || ''),
                        kind: 'list' as const,
                    }))
                    .filter((option: { id: string; title: string }) => option.id && option.title)
                : [],
        );
    }

    return [];
}

export default HourReportingSandboxPage;
